/**
 * HaynesPro confirmation API (#2279).
 *
 * Mirrors staff-api vehicle-technical-data routes. GET is capability only
 * (no vendor call). Candidate lookup is a user-triggered mutation.
 */

import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '@/lib/api';

export type HaynesProLanguage = 'fi' | 'en';
export type HaynesProLookupMode = 'vin' | 'details';
export type HaynesProSelectionMethod = 'vin' | 'details' | 'tree';
export type HaynesProTreeLevel = 'MAKE' | 'MODEL' | 'TYPE';
export type HaynesProAvailabilityReason =
  | 'ok'
  | 'disabled'
  | 'tenant_not_licensed'
  | 'expired_credentials'
  | 'invalid_config';
export type ComparisonFlag = 'matched' | 'mismatch' | 'missing' | 'unknown';
export type YearComparisonBasis = 'manufacturing' | 'unknown';

export const TECHNICAL_DATA_ERROR_CODES = [
  'disabled',
  'tenant_not_licensed',
  'expired_credentials',
  'invalid_config',
  'vehicle_not_found',
  'source_missing',
  'invalid_details',
  'no_data',
  'too_many_candidates',
  'candidate_not_found',
  'repair_type_invalid',
  'stale_fingerprint',
  'stale_revision',
  'confirmation_required',
  'operation_rights',
  'provider_auth',
  'provider_ban',
  'provider_error',
] as const;

export type TechnicalDataErrorCode = (typeof TECHNICAL_DATA_ERROR_CODES)[number];

export interface HaynesProDetailsInput {
  make?: string;
  model?: string;
  modelCode?: string;
  typeDescription?: string;
  engineCode?: string;
  engineCapacity?: number;
  engineOutput?: number;
  manufacturingYear?: number;
  treeVehicleId?: number;
  treeVehicleLevel?: HaynesProTreeLevel;
}

export interface HaynesProCandidatesBody {
  mode: HaynesProLookupMode;
  language: HaynesProLanguage;
  details?: HaynesProDetailsInput;
}

export interface HaynesProConfirmBody {
  confirmed: boolean;
  reason: string;
  language: HaynesProLanguage;
  method: HaynesProSelectionMethod;
  details?: HaynesProDetailsInput;
  carTypeId: number;
  repairtimeTypeId: number;
  typeCategory: string;
  rootNodeId: string;
  candidateId?: string;
  expectedSourceFingerprint: string;
  expectedCurrentVariantRevision: number;
}

export interface HaynesProRepairTypesBody {
  carTypeId: number;
  language: HaynesProLanguage;
  method: HaynesProSelectionMethod;
  details?: HaynesProDetailsInput;
  expectedSourceFingerprint: string;
}

export interface HaynesProRepairTypesResponse {
  carTypeId: number;
  repairTimeTypes: readonly NormalizedRepairTimeType[];
}

export interface ConfirmedHaynesProVariant {
  id: string;
  vehicleId: string;
  provider: 'haynespro';
  revision: number;
  carTypeId: number;
  repairtimeTypeId: number;
  typeCategory: string;
  rootNodeId: string;
  language: HaynesProLanguage;
  selectionMethod: HaynesProSelectionMethod;
  confirmationReason: string;
  confirmedBy: string;
  confirmedAt: string;
  validatedAt: string;
  dataVersion: string | null;
  sourceFingerprint: string;
  usable: boolean;
}

export interface HaynesProKnownDetails {
  make?: string | null;
  model?: string | null;
  engineCode?: string | null;
  engineCapacity?: number | null;
  engineOutput?: number | null;
}

export interface HaynesProCapability {
  available: boolean;
  reason: HaynesProAvailabilityReason;
  confirmedVariant: ConfirmedHaynesProVariant | null;
  currentSourceFingerprint: string;
  isStale: boolean;
  /** Additive GET field. Older responses may omit it. */
  knownDetails?: HaynesProKnownDetails;
}

export interface CandidateComparison {
  make: ComparisonFlag;
  model: ComparisonFlag;
  engineCode: ComparisonFlag;
  year: ComparisonFlag;
  capacity: ComparisonFlag;
  output: ComparisonFlag;
}

export interface NormalizedRepairTimeType {
  repairtimeTypeId: number;
  typeCategory: string;
  rootNodeId: string;
  type: string | null;
  make: string | null;
  model: string | null;
  madeFrom: string | null;
  madeUntil: string | null;
}

export interface NormalizedSubjectGroup {
  key: string;
  value: string;
}

export interface HaynesProCandidate {
  id: string;
  carTypeId: number;
  name: string | null;
  fullName: string | null;
  engineCode: string | null;
  fuelType: string | null;
  capacity: number | null;
  output: number | null;
  madeFrom: string | null;
  madeUntil: string | null;
  subjects: readonly string[];
  subjectsByGroup: readonly NormalizedSubjectGroup[];
  repairTimeTypes: readonly NormalizedRepairTimeType[];
  comparison: CandidateComparison;
  ambiguous: boolean;
}

export interface HaynesProLookupComparison {
  make: string | null;
  model: string | null;
  engineCode: string | null;
  manufacturingYear: number | null;
  capacity: number | null;
  output: number | null;
}

export interface HaynesProCandidatesResponse {
  lookupMethod: HaynesProSelectionMethod;
  fallbackUsed: 'details' | 'tree' | null;
  language: HaynesProLanguage;
  noData: boolean;
  ambiguous: boolean;
  autoSelected: boolean;
  candidateCount: number;
  candidates: readonly HaynesProCandidate[];
  comparison: HaynesProLookupComparison;
  yearComparisonBasis: YearComparisonBasis;
}

export interface HaynesProConfirmResponse {
  confirmedVariant: ConfirmedHaynesProVariant;
}

export interface ParsedTechnicalDataError {
  code: TechnicalDataErrorCode | 'unknown';
  status: number;
  currentSourceFingerprint?: string;
  retryAfterSeconds?: number;
  currentRevision?: number;
}

export interface HaynesProDetailsFormState {
  make: string;
  model: string;
  engineCode: string;
  engineCapacity: string;
  engineOutput: string;
  manufacturingYear: string;
}

export const HAYNESPRO_DETAILS_FORM_FIELDS = [
  'make',
  'model',
  'engineCode',
  'engineCapacity',
  'engineOutput',
  'manufacturingYear',
] as const;

export type HaynesProDetailsFormField = (typeof HAYNESPRO_DETAILS_FORM_FIELDS)[number];

export type HaynesProDetailsFormTouched = Partial<Record<HaynesProDetailsFormField, boolean>>;

export const vehicleTechnicalDataKeys = {
  haynespro: (vehicleId: string) =>
    ['vehicles', vehicleId, 'technical-data', 'haynespro'] as const,
};

const ERROR_CODE_SET = new Set<string>(TECHNICAL_DATA_ERROR_CODES);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function parseTechnicalDataError(error: unknown): ParsedTechnicalDataError {
  if (error instanceof ApiError) {
    const data = error.data;
    if (isRecord(data) && typeof data.error === 'string' && ERROR_CODE_SET.has(data.error)) {
      const parsed: ParsedTechnicalDataError = {
        code: data.error as TechnicalDataErrorCode,
        status: error.status,
      };
      if (typeof data.currentSourceFingerprint === 'string') {
        parsed.currentSourceFingerprint = data.currentSourceFingerprint;
      }
      if (typeof data.retryAfterSeconds === 'number') {
        parsed.retryAfterSeconds = data.retryAfterSeconds;
      }
      if (typeof data.currentRevision === 'number') {
        parsed.currentRevision = data.currentRevision;
      }
      return parsed;
    }
    return { code: 'unknown', status: error.status };
  }
  return { code: 'unknown', status: 0 };
}

export function haynesProUiLanguage(language: string): HaynesProLanguage {
  return language.toLowerCase().startsWith('en') ? 'en' : 'fi';
}

function parseOptionalInt(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isSafeInteger(parsed) ? parsed : undefined;
}

export function emptyHaynesProDetailsForm(
  make?: string | null,
  model?: string | null,
): HaynesProDetailsFormState {
  return {
    make: (make ?? '').trim(),
    model: (model ?? '').trim(),
    engineCode: '',
    engineCapacity: '',
    engineOutput: '',
    manufacturingYear: '',
  };
}

export function cloneHaynesProDetailsForm(
  form: HaynesProDetailsFormState,
): HaynesProDetailsFormState {
  return {
    make: form.make,
    model: form.model,
    engineCode: form.engineCode,
    engineCapacity: form.engineCapacity,
    engineOutput: form.engineOutput,
    manufacturingYear: form.manufacturingYear,
  };
}

export function haynesProDetailsFormEquals(
  a: HaynesProDetailsFormState,
  b: HaynesProDetailsFormState,
): boolean {
  return (
    a.make === b.make &&
    a.model === b.model &&
    a.engineCode === b.engineCode &&
    a.engineCapacity === b.engineCapacity &&
    a.engineOutput === b.engineOutput &&
    a.manufacturingYear === b.manufacturingYear
  );
}

function knownDetailsHas(
  known: HaynesProKnownDetails,
  key: keyof HaynesProKnownDetails,
): boolean {
  return Object.prototype.hasOwnProperty.call(known, key) && known[key] !== undefined;
}

function knownDetailsText(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** Explicit 0 is a known value. Non-integers and missing values stay blank. */
function knownDetailsNumberString(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) return '';
  return String(value);
}

function hydrateKnownText(
  current: string,
  known: HaynesProKnownDetails,
  key: 'make' | 'model' | 'engineCode',
  touched: boolean,
): string {
  if (touched) return current;
  if (!knownDetailsHas(known, key)) return current;
  return knownDetailsText(known[key]);
}

function hydrateKnownNumber(
  current: string,
  known: HaynesProKnownDetails,
  key: 'engineCapacity' | 'engineOutput',
  touched: boolean,
): string {
  if (touched) return current;
  if (!knownDetailsHas(known, key)) return current;
  return knownDetailsNumberString(known[key]);
}

/**
 * Fill untouched search fields from GET knownDetails. Manufacturing year is
 * never inferred. Returns `current` when nothing changed so React can skip.
 */
export function mergeHaynesProKnownDetailsIntoForm(
  current: HaynesProDetailsFormState,
  known: HaynesProKnownDetails | null | undefined,
  touched: HaynesProDetailsFormTouched,
): HaynesProDetailsFormState {
  if (!known) return current;
  const next: HaynesProDetailsFormState = {
    make: hydrateKnownText(current.make, known, 'make', Boolean(touched.make)),
    model: hydrateKnownText(current.model, known, 'model', Boolean(touched.model)),
    engineCode: hydrateKnownText(current.engineCode, known, 'engineCode', Boolean(touched.engineCode)),
    engineCapacity: hydrateKnownNumber(
      current.engineCapacity,
      known,
      'engineCapacity',
      Boolean(touched.engineCapacity),
    ),
    engineOutput: hydrateKnownNumber(
      current.engineOutput,
      known,
      'engineOutput',
      Boolean(touched.engineOutput),
    ),
    manufacturingYear: current.manufacturingYear,
  };
  return haynesProDetailsFormEquals(current, next) ? current : next;
}

export function applyHaynesProDetailsPrefill(input: {
  current: HaynesProDetailsFormState;
  knownDetails?: HaynesProKnownDetails | null;
  touched: HaynesProDetailsFormTouched;
  fallbackMake?: string | null;
  fallbackModel?: string | null;
}): HaynesProDetailsFormState {
  const { current, knownDetails, touched, fallbackMake, fallbackModel } = input;
  if (knownDetails) {
    return mergeHaynesProKnownDetailsIntoForm(current, knownDetails, touched);
  }
  const next: HaynesProDetailsFormState = {
    ...current,
    make: touched.make ? current.make : current.make || (fallbackMake ?? '').trim(),
    model: touched.model ? current.model : current.model || (fallbackModel ?? '').trim(),
  };
  return haynesProDetailsFormEquals(current, next) ? current : next;
}

export function haynesProDetailsFormTouchedFromChange(
  previous: HaynesProDetailsFormState,
  next: HaynesProDetailsFormState,
  already: HaynesProDetailsFormTouched,
): HaynesProDetailsFormTouched {
  const touched: HaynesProDetailsFormTouched = { ...already };
  for (const field of HAYNESPRO_DETAILS_FORM_FIELDS) {
    if (previous[field] !== next[field]) touched[field] = true;
  }
  return touched;
}

export function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

export function buildHaynesProDetails(
  form: HaynesProDetailsFormState,
): HaynesProDetailsInput | undefined {
  const details: HaynesProDetailsInput = {};
  const make = form.make.trim();
  const model = form.model.trim();
  const engineCode = form.engineCode.trim();
  const engineCapacity = parseOptionalInt(form.engineCapacity);
  const engineOutput = parseOptionalInt(form.engineOutput);
  const manufacturingYear = parseOptionalInt(form.manufacturingYear);
  if (make) details.make = make;
  if (model) details.model = model;
  if (engineCode) details.engineCode = engineCode;
  if (engineCapacity !== undefined) details.engineCapacity = engineCapacity;
  if (engineOutput !== undefined) details.engineOutput = engineOutput;
  if (manufacturingYear !== undefined) details.manufacturingYear = manufacturingYear;
  return Object.keys(details).length > 0 ? details : undefined;
}

const VERIFICATION_FLAGS: ReadonlySet<ComparisonFlag> = new Set(['mismatch', 'missing']);

export function candidateNeedsVerification(comparison: CandidateComparison): boolean {
  return (
    VERIFICATION_FLAGS.has(comparison.make) ||
    VERIFICATION_FLAGS.has(comparison.model) ||
    VERIFICATION_FLAGS.has(comparison.engineCode) ||
    VERIFICATION_FLAGS.has(comparison.year) ||
    VERIFICATION_FLAGS.has(comparison.capacity) ||
    VERIFICATION_FLAGS.has(comparison.output)
  );
}

export function repairTypeKey(type: {
  repairtimeTypeId: number;
  typeCategory: string;
  rootNodeId: string;
}): string {
  return `${String(type.repairtimeTypeId)}:${type.typeCategory}:${type.rootNodeId}`;
}

export function parseRepairTypeKey(key: string): {
  repairtimeTypeId: number;
  typeCategory: string;
  rootNodeId: string;
} | null {
  const firstSep = key.indexOf(':');
  const secondSep = key.indexOf(':', firstSep + 1);
  if (firstSep <= 0 || secondSep <= firstSep) return null;
  const repairtimeTypeId = Number.parseInt(key.slice(0, firstSep), 10);
  const typeCategory = key.slice(firstSep + 1, secondSep);
  const rootNodeId = key.slice(secondSep + 1);
  if (!Number.isSafeInteger(repairtimeTypeId) || repairtimeTypeId <= 0) return null;
  if (!typeCategory || !rootNodeId) return null;
  return { repairtimeTypeId, typeCategory, rootNodeId };
}

export function isStaleConflict(code: TechnicalDataErrorCode | 'unknown'): boolean {
  return code === 'stale_fingerprint' || code === 'stale_revision';
}

function haynesProPath(vehicleId: string, suffix = ''): string {
  return `/vehicles/${vehicleId}/technical-data/haynespro${suffix}`;
}

export function useVehicleTechnicalData(vehicleId: string | undefined) {
  const queryClient = useQueryClient();
  const lookupAbortRef = useRef<AbortController | null>(null);
  const confirmAbortRef = useRef<AbortController | null>(null);
  const repairTypesAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      lookupAbortRef.current?.abort();
      confirmAbortRef.current?.abort();
      repairTypesAbortRef.current?.abort();
      lookupAbortRef.current = null;
      confirmAbortRef.current = null;
      repairTypesAbortRef.current = null;
    };
  }, [vehicleId]);

  const capabilityQuery = useQuery({
    queryKey: vehicleTechnicalDataKeys.haynespro(vehicleId ?? ''),
    queryFn: () => api.get<HaynesProCapability>(haynesProPath(vehicleId ?? '')),
    enabled: Boolean(vehicleId),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const candidatesMutation = useMutation({
    mutationFn: (body: HaynesProCandidatesBody) => {
      if (!vehicleId) {
        return Promise.reject(new Error('vehicleId is required'));
      }
      lookupAbortRef.current?.abort();
      repairTypesAbortRef.current?.abort();
      const controller = new AbortController();
      lookupAbortRef.current = controller;
      return api.post<HaynesProCandidatesResponse>(
        haynesProPath(vehicleId, '/candidates'),
        body,
        { signal: controller.signal },
      );
    },
  });

  const repairTypesMutation = useMutation({
    mutationFn: (body: HaynesProRepairTypesBody) => {
      if (!vehicleId) {
        return Promise.reject(new Error('vehicleId is required'));
      }
      repairTypesAbortRef.current?.abort();
      const controller = new AbortController();
      repairTypesAbortRef.current = controller;
      return api.post<HaynesProRepairTypesResponse>(
        haynesProPath(vehicleId, '/repair-types'),
        body,
        { signal: controller.signal },
      );
    },
  });

  const confirmMutation = useMutation({
    mutationFn: (body: HaynesProConfirmBody) => {
      if (!vehicleId) {
        return Promise.reject(new Error('vehicleId is required'));
      }
      confirmAbortRef.current?.abort();
      const controller = new AbortController();
      confirmAbortRef.current = controller;
      return api.post<HaynesProConfirmResponse>(
        haynesProPath(vehicleId, '/confirm'),
        body,
        { signal: controller.signal },
      );
    },
    onSuccess: async () => {
      if (!vehicleId) return;
      await queryClient.invalidateQueries({
        queryKey: vehicleTechnicalDataKeys.haynespro(vehicleId),
      });
    },
  });

  return {
    capabilityQuery,
    candidatesMutation,
    repairTypesMutation,
    confirmMutation,
  };
}
